import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';
import {
  type FetchPaymentStateParams,
  type FetchPaymentStateResponse,
} from '@sg/shared-fe-features-ordering';

import {
  SweetpassPaymentStateDocument,
  type SweetpassPaymentStateQuery,
  type SweetpassPaymentStateQueryVariables,
} from './graphql/SweetpassPaymentState.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches the order payment state of a sweetpass purchase, which is resolved
 * asynchronously by the backend after Stripe confirms the payment.
 */
export const useFetchPaymentStateActor = () => {
  const client = useClient();

  const fetchPaymentState = useMemo(
    () =>
      fromPromise<FetchPaymentStateResponse, FetchPaymentStateParams>(async ({ input }) => {
        const { orderId } = input;

        if (!orderId) throw new Error('Missing order id');

        const response = await client
          .query<SweetpassPaymentStateQuery, SweetpassPaymentStateQueryVariables>(
            SweetpassPaymentStateDocument,
            { orderId, orderType: OrderTypeValues.SweetpassOrder },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        const paymentState = response.data?.orderPaymentState;

        if (!paymentState) {
          throw response.error ?? new Error('Sweetpass order payment state not found');
        }

        const updatedAt = Number(paymentState.updatedAt);

        return {
          orderId,
          paymentIntentId: paymentState.paymentIntentId,
          paymentStateExpiration: Number.isNaN(updatedAt) ? undefined : updatedAt,
          paymentStateStatus: paymentState.status,
          paymentStateError: paymentState.error,
        };
      }),
    [client],
  );

  return { fetchPaymentState };
};
